/**
 * planCoverage — WHAT'S COVERED, the brain (PLAN-COVERAGE-TALKS, 34i91).
 *
 * Seth (2026-10-21): "If I click an item that's not included, should it verify
 * it's part of the plan and if it's not, suggest the price increase, and if
 * approved the plan adjusts? Basically making everything talk. And the notes
 * would be adjusted for the future plan tickets of what's included."
 *
 * Everything talks through ONE derived model:
 *   equipment list + the plan's checks  → which SYSTEMS are covered
 *   ESC's agreement parts / the Pegasus sale's lines / Pegasus plan accessories
 *                                       → which PARTS are included
 *   the plan price catalog              → what a not-included row would COST
 *   pegasus_plan_adjustment             → what the office APPROVED (add to the
 *                                         plan from the next renewal / now, or
 *                                         charge at the visit), undoable
 *   the dispatch notes                  → GHOST units ("switch out the breathe
 *                                         clean to an air scrubber")
 * and the next tune-up TICKET is composed from the same model (Bring · Charge
 * · Offer), so Parts for the Day and the tech read what the card shows.
 *
 * How ServiceTitan / FieldEdge / Housecall / Salesforce do it — adopted: coverage
 * per piece of equipment (ServiceTitan), parts per task as the included list
 * (FieldEdge = ESC's own agr_parts), "add to plan" at the moment of the charge
 * (Housecall), a recorded, prorated amendment for mid-term changes (Salesforce
 * CPQ). Rejected: free-text task lines as the source of truth.
 *
 * ESC stays read-only (DEC-22): an ESC plan's adjustment is recorded here and
 * priced into the renewal; until cutover the office invoices it in ESC.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { ClassifiedEquip, EquipCategory, EquipmentView } from "@/lib/equipment";
import type { PlanPrice } from "@/features/agreements/PlanBuilder";
import type { IncludedPart, PlanAccessory } from "@/features/customer-360/PlanAccessoriesEditor";
import { humidifierPad, planPartKind, type PartKind } from "@/lib/truckParts";

// ─── the recorded word ────────────────────────────────────────────────────────
export type AdjustKind = "add" | "charge_visit" | "remove";
export type AdjustEffective = "next_renewal" | "now" | "visit";
export interface PlanAdjustment {
  id: string;
  custno: string;
  locno: string | null;
  agrmt_no: string | null;
  plan_sale_id: string | null;
  kind: AdjustKind;
  catalog_id: string | null;
  label: string;
  equip_counter: string | null;
  amount: number;
  effective: AdjustEffective;
  prorated_amount: number | null;
  effective_date: string | null;
  note: string | null;
  approved_by: string | null;
  approved_at: string;
  undone_at: string | null;
}

export type PlanRef = { kind: "esc"; agrmtNo: string } | { kind: "pegasus"; saleId: string };

export function usePlanAdjustments(custno: string | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["plan_adjustments", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 20 * 1000,
    queryFn: async (): Promise<PlanAdjustment[]> => {
      let q = supabase.from("pegasus_plan_adjustment").select("*").eq("custno", custno!).is("undone_at", null).order("approved_at", { ascending: false });
      if (locno) q = q.eq("locno", locno);
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
        ...(r as unknown as PlanAdjustment),
        amount: Number(r.amount ?? 0),
        prorated_amount: r.prorated_amount == null ? null : Number(r.prorated_amount),
      }));
    },
  });
}

export interface ApproveInput {
  custno: string;
  locno: string | null;
  plan: PlanRef;
  kind: AdjustKind;
  catalogId: string | null;
  label: string;
  equipCounter: string | null;
  amount: number;
  effective: AdjustEffective;
  proratedAmount?: number | null;
  effectiveDate?: string | null;
  note?: string | null;
  /** A Pegasus sale, effective now: the sale itself takes the line + the new total. */
  saleLine?: { kind: "item" | "addon"; id: string | null; name: string; price: number } | null;
}

/** Approve a change to the plan — the recorded adjustment, plus the Pegasus sale's own update when it starts now. */
export function useApproveAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: ApproveInput): Promise<string> => {
      const { data: u } = await supabase.auth.getUser();
      const who = u?.user?.email ?? null;
      const { data, error } = await supabase.from("pegasus_plan_adjustment").insert({
        custno: v.custno, locno: v.locno,
        agrmt_no: v.plan.kind === "esc" ? v.plan.agrmtNo : null,
        plan_sale_id: v.plan.kind === "pegasus" ? v.plan.saleId : null,
        kind: v.kind, catalog_id: v.catalogId, label: v.label, equip_counter: v.equipCounter,
        amount: v.amount, effective: v.effective, prorated_amount: v.proratedAmount ?? null,
        effective_date: v.effectiveDate ?? null, note: v.note ?? null, approved_by: who,
      }).select("id").single();
      if (error) throw error;
      // A Pegasus-sold plan that changes NOW changes in place: the line joins the
      // sale and the total moves. (From the next renewal: the renewal is priced
      // from the adjustments; the current sale stays as sold.)
      if (v.plan.kind === "pegasus" && v.kind === "add" && v.effective === "now" && v.saleLine) {
        const { data: sale } = await supabase.from("pegasus_plan_sale").select("item_lines, addon_ids, total").eq("id", v.plan.saleId).maybeSingle();
        if (sale) {
          const s = sale as { item_lines: unknown[] | null; addon_ids: string[] | null; total: number | null };
          const patch: Record<string, unknown> = { total: Number(s.total ?? 0) + v.amount };
          if (v.saleLine.kind === "item") patch.item_lines = [...(s.item_lines ?? []), { id: v.saleLine.id, name: v.saleLine.name, qty: 1, price: v.saleLine.price, custom: false, adjustment: (data as { id: string }).id }];
          else if (v.saleLine.id) patch.addon_ids = [...new Set([...(s.addon_ids ?? []), v.saleLine.id])];
          const { error: e2 } = await supabase.from("pegasus_plan_sale").update(patch).eq("id", v.plan.saleId);
          if (e2) throw e2;
        }
      }
      return (data as { id: string }).id;
    },
    onSuccess: (_id, v) => {
      qc.invalidateQueries({ queryKey: ["plan_adjustments", v.custno] });
      qc.invalidateQueries({ queryKey: ["pegasus_agreements", v.custno] });
    },
  });
}

/** Undo an approved change (24-hour window in the UI; the row keeps its history). */
export function useUndoAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { adj: PlanAdjustment }) => {
      const { data: u } = await supabase.auth.getUser();
      const who = u?.user?.email ?? null;
      const { error } = await supabase.from("pegasus_plan_adjustment").update({ undone_at: new Date().toISOString(), undone_by: who }).eq("id", v.adj.id);
      if (error) throw error;
      if (v.adj.plan_sale_id && v.adj.kind === "add" && v.adj.effective === "now") {
        const { data: sale } = await supabase.from("pegasus_plan_sale").select("item_lines, addon_ids, total").eq("id", v.adj.plan_sale_id).maybeSingle();
        if (sale) {
          const s = sale as { item_lines: ({ adjustment?: string } & Record<string, unknown>)[] | null; addon_ids: string[] | null; total: number | null };
          const patch: Record<string, unknown> = { total: Math.max(0, Number(s.total ?? 0) - v.adj.amount) };
          patch.item_lines = (s.item_lines ?? []).filter((l) => l.adjustment !== v.adj.id);
          if (v.adj.catalog_id) patch.addon_ids = (s.addon_ids ?? []).filter((id) => id !== v.adj.catalog_id);
          const { error: e2 } = await supabase.from("pegasus_plan_sale").update(patch).eq("id", v.adj.plan_sale_id);
          if (e2) throw e2;
        }
      }
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["plan_adjustments", v.adj.custno] });
      qc.invalidateQueries({ queryKey: ["pegasus_agreements", v.adj.custno] });
    },
  });
}

// ─── the notes, for ghosts ────────────────────────────────────────────────────
export interface HomeNote { date: string | null; notes: string | null; dispatch: string | null }
export function useHomeNotes(custno: string | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["home_notes", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<HomeNote[]> => {
      let q = supabase.from("v_dispatch_full").select("dispatch, appointment_date, notes").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      const { data } = await q.order("appointment_date", { ascending: false }).limit(120);
      return ((data ?? []) as { dispatch: string | null; appointment_date: string | null; notes: string | null }[])
        .map((r) => ({ date: r.appointment_date, notes: r.notes, dispatch: r.dispatch }));
    },
  });
}

/** A unit the notes say was swapped out: "switch out the breathe clean to an air scrubber". */
export interface Ghost { by: string; date: string | null; dispatch: string | null }
// "switch out the breathe clean to an air scrubber $989" → from "breathe clean", by "air scrubber".
// The BY phrase runs to a real stop — a price, a number, punctuation, a joining word — not the next space.
const SWAP_RE = /\b(?:switch(?:ed|ing)?\s+out|replac(?:e|ed|ing)|swap(?:ped|ping)?(?:\s+out)?|remov(?:e|ed|ing)|took\s+out|take\s+out)\s+(?:the\s+|old\s+|their\s+|his\s+|her\s+)?([a-z][a-z0-9 /&-]{2,36}?)\s+(?:to|with|for)\s+(?:(?:a|an|the|new|another|brand new)\s+)*([a-z][a-z /&-]{2,36}?)(?=\s*(?:\$|\d|[,.;:)!?]|\s+(?:for|and|at|on|in|to|with|per|plus)\b)|\s*$)/gi;
/** The words a unit answers to in a note — its make and the first word of its model. */
function unitWords(e: ClassifiedEquip): string[] {
  const words = new Set<string>();
  const push = (s: string | null | undefined) => {
    for (const w of (s ?? "").toLowerCase().split(/[^a-z]+/)) if (w.length >= 4) words.add(w);
  };
  push(e.mfg);
  push((e.model ?? "").split(/\s+/).slice(0, 2).join(" "));
  // Category words count only when they are specific (not "equipment").
  if (e.category === "uv" || e.category === "air-cleaner") words.add("scrubber");
  return [...words].filter((w) => !["unit", "model", "equipment", "system", "lennox", "carrier", "aprilaire", "honeywell"].includes(w));
}
export function findGhosts(notes: HomeNote[], units: ClassifiedEquip[]): Map<string, Ghost> {
  const out = new Map<string, Ghost>();
  for (const u of units) {
    const words = unitWords(u);
    if (words.length === 0) continue;
    for (const n of notes) {
      if (!n.notes) continue;
      if (u.install_date && n.date && n.date < u.install_date) continue;
      SWAP_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = SWAP_RE.exec(n.notes)) !== null) {
        const from = m[1].toLowerCase();
        if (!words.some((w) => from.includes(w))) continue;
        const by = m[2].replace(/\s+/g, " ").trim();
        // "replaced the filter with a new one" is not a unit swap.
        if (/^(new one|same|one)$/i.test(by) || /filter|pad|bulb|belt|motor|capacitor|igniter|thermostat/i.test(from)) continue;
        out.set(u.counter, { by: by.replace(/\b\w/g, (c) => c.toUpperCase()), date: n.date, dispatch: n.dispatch });
        break;
      }
      if (out.has(u.counter)) break;
    }
  }
  return out;
}

// ─── pricing ─────────────────────────────────────────────────────────────────
export interface PriceOffer {
  catalogId: string | null;
  catalogKind: "item" | "addon" | "pad";
  label: string;        // "Air Scrubber bulbs"
  amount: number;       // the term delta (flat for an item, the term column for an add-on; 0 for a pad)
  visitAmount: number | null; // what it costs on a ticket instead (items only)
}
const TERM_COL: Record<number, keyof PlanPrice> = { 1: "p1", 2: "p2", 3: "p3", 5: "p5", 10: "p10" };
function termPrice(p: PlanPrice, years: number | null): number {
  const y = years && years > 0 ? years : 1;
  const col = TERM_COL[y];
  const v = col ? (p[col] as number | null) : null;
  if (v != null) return Number(v);
  // Off-menu term (4, 6…): the catalog's 1-year price per year.
  return Number(p.p1 ?? 0) * y;
}
function findCatalog(catalog: PlanPrice[], kind: "item" | "addon", re: RegExp): PlanPrice | null {
  return catalog.find((p) => p.kind === kind && p.active !== false && re.test(p.name)) ?? null;
}
/** What a not-included unit would cost to put on the plan, by its make/model/category. */
export function offerFor(e: ClassifiedEquip, catalog: PlanPrice[], termYears: number | null): PriceOffer | null {
  const text = `${e.mfg ?? ""} ${e.model ?? ""}`.toUpperCase();
  if (e.category === "uv" || (e.category === "air-cleaner" && /BREATHE|SCRUB|PURIF|UV|REME|HALO/.test(text))) {
    const item = /SCRUB/.test(text) ? findCatalog(catalog, "item", /scrubber/i)
      : /BREATHE/.test(text) ? findCatalog(catalog, "item", /breathe/i)
      : findCatalog(catalog, "item", /bulb/i);
    if (!item) return null;
    return { catalogId: item.id, catalogKind: "item", label: item.name, amount: Number(item.flat ?? 0), visitAmount: Number(item.flat ?? 0) };
  }
  if (e.category === "garage-heater") {
    const item = findCatalog(catalog, "item", /garage/i);
    return item ? { catalogId: item.id, catalogKind: "item", label: item.name, amount: Number(item.flat ?? 0), visitAmount: null } : null;
  }
  if (e.category === "erv-hrv") {
    const add = /\bERV\b|ENERGY/.test(text) ? findCatalog(catalog, "addon", /^ERV/i) : findCatalog(catalog, "addon", /^HRV/i);
    return add ? { catalogId: add.id, catalogKind: "addon", label: add.name, amount: termPrice(add, termYears), visitAmount: null } : null;
  }
  if (e.category === "humidifier") {
    const pad = humidifierPad(e.model) ?? "APR35";
    return { catalogId: null, catalogKind: "pad", label: `${pad} water panel`, amount: 0, visitAmount: null };
  }
  return null;
}
/** The rest of this term, in whole months (at least 1 while the plan is in force). */
export function monthsLeft(termEnd: string | null, today = new Date()): number | null {
  if (!termEnd) return null;
  const end = new Date(termEnd + "T00:00:00");
  const m = (end.getFullYear() - today.getFullYear()) * 12 + (end.getMonth() - today.getMonth());
  return m < 0 ? 0 : Math.max(1, m);
}
export function prorate(amount: number, termYears: number | null, termEnd: string | null): { amount: number; left: number; total: number } | null {
  const left = monthsLeft(termEnd);
  const total = (termYears && termYears > 0 ? termYears : 1) * 12;
  if (left == null || left <= 0) return null;
  return { amount: Math.round((amount * Math.min(left, total)) / total), left: Math.min(left, total), total };
}

// ─── the coverage model ──────────────────────────────────────────────────────
export type CoverState = "covered" | "included" | "added" | "not_covered" | "chargeable" | "charge_approved" | "ghost" | "not_plan_item";

export interface CoverRow {
  key: string;
  equip: ClassifiedEquip;
  state: CoverState;
  /** the fact beside the state — "Furnace Check · each fall", "panel included", "replaced the Breathe Clean" */
  why: string | null;
  offer: PriceOffer | null;
  ghost: Ghost | null;
  adjustment: PlanAdjustment | null;
}
export interface TicketLine { label: string; qty?: number; kind?: PartKind; amount?: number }
export interface TicketPreview {
  check: string | null;
  when: string | null;
  bring: TicketLine[];
  charge: TicketLine[];
  offer: TicketLine[];
}
export interface Coverage {
  systems: CoverRow[];
  accessories: CoverRow[];
  parts: IncludedPart[];
  ticket: TicketPreview;
  summary: { systems: number; parts: number; charges: number; settle: number };
}

/** Equipment categories a given included check covers (the AGR-03c matching). */
export function checkCategories(label: string): EquipCategory[] {
  const h = label.toLowerCase();
  if (/mini/.test(h)) return ["mini-split"];
  if (/furnace|boiler|heat/.test(h)) return ["furnace", "boiler"];
  if (/a\/?c|cool/.test(h)) return ["ac", "coil", "heat-pump", "mini-split", "rooftop"];
  return [];
}
const CHECK_WORD: Partial<Record<EquipCategory, string>> = {
  furnace: "Furnace Check · each fall",
  boiler: "Boiler Check · each fall",
  ac: "A/C Check · each spring",
  "heat-pump": "A/C Check · each spring",
  rooftop: "A/C Check · each spring",
  "mini-split": "Mini-Split Check · each spring",
  coil: "with the A/C Check",
};
const NOT_PLAN_ITEMS = new Set<EquipCategory>(["thermostat", "water-heater", "rds", "consumable", "material", "other"]);

export interface CoverageInput {
  equipView: EquipmentView;
  taskLabels: string[];
  includedParts: IncludedPart[];      // ESC's agreement parts or the Pegasus sale's lines
  accessories: PlanAccessory[];       // pegasus_plan_accessory rows for the home
  adjustments: PlanAdjustment[];      // live rows for the home
  plan: PlanRef;
  catalog: PlanPrice[];
  termYears: number | null;
  termEnd: string | null;
  ghosts: Map<string, Ghost>;
  nextCheck: { label: string; when: string } | null;
  /** the office's per-unit checkbox from before (on_plan override) — honored as the final word */
  onPlanOverride?: (counter: string) => boolean | null;
}

export function buildCoverage(i: CoverageInput): Coverage {
  const mine = (a: PlanAdjustment) => (i.plan.kind === "esc" ? a.agrmt_no === i.plan.agrmtNo : a.plan_sale_id === i.plan.saleId);
  const adjs = i.adjustments.filter(mine);
  const adjFor = (e: ClassifiedEquip) => adjs.find((a) => a.equip_counter === e.counter && a.kind !== "remove") ?? null;
  const coveredCats = new Set<EquipCategory>(i.taskLabels.flatMap(checkCategories));

  // The parts the plan includes — ESC rows / sale lines, the home's Pegasus
  // accessories, and items added NOW by an approved adjustment.
  const parts: IncludedPart[] = [...i.includedParts];
  const seen = new Set(parts.map((p) => norm(p.code)));
  for (const a of i.accessories) {
    if (seen.has(norm(a.label))) continue;
    seen.add(norm(a.label));
    parts.push({ code: a.label, qty: a.qty, description: null, item_id: a.item_id, kind: (a.kind as PartKind) ?? planPartKind(a.label), source: "pegasus" });
  }
  for (const a of adjs) {
    if (a.kind !== "add" || a.effective !== "now") continue;
    if (seen.has(norm(a.label))) continue;
    seen.add(norm(a.label));
    parts.push({ code: a.label, qty: 1, description: `added ${shortDate(a.approved_at)}`, item_id: null, kind: planPartKind(a.label), source: "pegasus" });
  }
  const partKinds = new Set(parts.map((p) => p.kind));
  const partsSay = (re: RegExp) => parts.some((p) => re.test(`${p.code} ${p.description ?? ""}`));

  // The unit a ghost was swapped FOR reads "replaced the Breathe Clean".
  const replacedWhat = new Map<string, string>();
  const allUnits = [...i.equipView.systems, ...i.equipView.auxiliary];
  for (const [counter, g] of i.ghosts) {
    const old = allUnits.find((u) => u.counter === counter);
    if (!old) continue;
    const byWords = g.by.toLowerCase().split(/[^a-z]+/).filter((w) => w.length >= 4);
    const target = allUnits.find((u) => u.counter !== counter && unitWords(u).some((w) => byWords.includes(w)));
    if (target) replacedWhat.set(target.counter, `replaced the ${titleWords(old.mfg || old.model || old.categoryLabel)}`);
  }
  const rowFor = (e: ClassifiedEquip, group: "system" | "accessory"): CoverRow => {
    const ghost = i.ghosts.get(e.counter) ?? null;
    const adj = adjFor(e);
    const ov = i.onPlanOverride?.(e.counter) ?? null;
    const base: CoverRow = { key: e.counter, equip: e, state: "not_covered", why: null, offer: null, ghost, adjustment: adj };
    if (ghost && !e.replaced) return { ...base, state: "ghost", why: `swapped for the ${ghost.by}${ghost.date ? ` · ${longDate(ghost.date)}` : ""} · from the call notes` };
    if (adj) {
      if (adj.kind === "charge_visit") return { ...base, state: "charge_approved", why: `${adj.label} · ${money(adj.amount)} on the next ticket`, offer: offerFor(e, i.catalog, i.termYears) };
      return { ...base, state: "added", why: adj.effective === "now" ? `${adj.label} · on the plan since ${shortDate(adj.approved_at)}` : `${adj.label} · from the ${adj.effective_date ? longDate(adj.effective_date) : "next"} renewal` };
    }
    if (ov === false) return { ...base, state: "not_covered", why: "left off by the office", offer: offerFor(e, i.catalog, i.termYears) };
    if (group === "system") {
      if (coveredCats.has(e.category) || ov === true) return { ...base, state: "covered", why: CHECK_WORD[e.category] ?? "on the plan" };
      if (NOT_PLAN_ITEMS.has(e.category)) return { ...base, state: "not_plan_item", why: null };
      return { ...base, state: "not_covered", why: null, offer: offerFor(e, i.catalog, i.termYears) };
    }
    // accessories & indoor air
    if (NOT_PLAN_ITEMS.has(e.category)) return { ...base, state: "not_plan_item", why: null };
    if (e.category === "humidifier") {
      const pad = humidifierPad(e.model);
      if (partKinds.has("humidifier") || ov === true) return { ...base, state: "covered", why: pad ? `takes the ${pad.replace("APR", "#")} panel · included` : "panel included" };
      return { ...base, state: "not_covered", why: pad ? `takes the ${pad.replace("APR", "#")} panel` : null, offer: offerFor(e, i.catalog, i.termYears) };
    }
    const offer = offerFor(e, i.catalog, i.termYears);
    if (offer && offer.catalogKind === "item" && /bulb/i.test(offer.label)) {
      if (partsSay(/BULB/i) || ov === true) return { ...base, state: "included", why: replacedWhat.get(e.counter) ?? "bulbs included" };
      return { ...base, state: "chargeable", why: replacedWhat.get(e.counter) ?? null, offer };
    }
    if (e.category === "air-cleaner") {
      if (partKinds.has("aprilaire") || partKinds.has("filter") || ov === true) return { ...base, state: "covered", why: "media included" };
      return { ...base, state: "not_covered", why: "add its media as a part", offer: null };
    }
    if (offer) return { ...base, state: "not_covered", why: null, offer };
    return { ...base, state: "not_covered", why: null, offer: null };
  };

  const systems = i.equipView.systems.map((e) => rowFor(e, "system"));
  const accessories = i.equipView.auxiliary.map((e) => rowFor(e, "accessory"));

  // The next tune-up ticket, composed from the same model.
  const bring: TicketLine[] = parts.map((p) => ({ label: p.code, qty: p.qty, kind: p.kind }));
  const charge: TicketLine[] = adjs.filter((a) => a.kind === "charge_visit").map((a) => ({ label: a.label, amount: a.amount }));
  const offer: TicketLine[] = accessories.filter((r) => r.state === "chargeable" && r.offer).map((r) => ({ label: r.offer!.label, amount: r.offer!.visitAmount ?? r.offer!.amount }));
  const ticket: TicketPreview = { check: i.nextCheck?.label ?? null, when: i.nextCheck?.when ?? null, bring, charge, offer };

  const all = [...systems, ...accessories];
  return {
    systems, accessories, parts, ticket,
    summary: {
      systems: systems.filter((r) => r.state === "covered" || r.state === "added").length,
      parts: parts.length,
      charges: all.filter((r) => r.state === "chargeable" || r.state === "charge_approved").length,
      settle: all.filter((r) => r.state === "ghost").length,
    },
  };
}

/** The ticket lines as one note line — what the dispatch carries into the day. */
export function ticketNoteLine(t: TicketPreview): string | null {
  const parts: string[] = [];
  if (t.bring.length) parts.push(`Bring: ${t.bring.map((b) => `${b.label}${b.qty && b.qty > 1 ? ` ×${b.qty}` : ""}`).join(", ")}`);
  if (t.charge.length) parts.push(`Charge: ${t.charge.map((c) => `${c.label} ${money(c.amount ?? 0)}`).join(", ")}`);
  if (t.offer.length) parts.push(`Offer: ${t.offer.map((c) => `${c.label} ${money(c.amount ?? 0)}`).join(", ")}`);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * For a REAL tune-up ticket (useScheduleTuneup): the plan's Bring + Charge lines
 * for this home, from the database alone — ESC's agreement parts on the active
 * agreement, the home's Pegasus plan accessories, the Pegasus sale's lines, and
 * the approved adjustments. (The Offer line needs the equipment list and the
 * catalog; the card's preview carries it, the ticket gets Bring and Charge.)
 */
export async function loadPlanTicketLines(custno: string, locno: string | null): Promise<{ note: string | null; parts: TicketLine[] }> {
  const [agr, acc, sales, adj] = await Promise.all([
    supabase.from("mirror_serv_agr").select("agrmt_no, locno").eq("custno", custno).eq("sa_inactive", 0),
    (locno ? supabase.from("pegasus_plan_accessory").select("label, qty, kind").eq("custno", custno).eq("locno", locno) : supabase.from("pegasus_plan_accessory").select("label, qty, kind").eq("custno", custno)),
    supabase.from("pegasus_plan_sale").select("id, locno, item_lines").eq("custno", custno).eq("status", "sold"),
    supabase.from("pegasus_plan_adjustment").select("kind, label, amount, effective, locno").eq("custno", custno).is("undone_at", null),
  ]);
  const agrNos = ((agr.data ?? []) as { agrmt_no: string; locno: string }[]).filter((a) => !locno || a.locno === locno).map((a) => a.agrmt_no);
  const bring = new Map<string, TicketLine>();
  const add = (label: string, qty: number, kind?: PartKind) => {
    const k = norm(label);
    if (!k) return;
    const cur = bring.get(k);
    if (cur) cur.qty = (cur.qty ?? 1) + qty; else bring.set(k, { label, qty, kind: kind ?? planPartKind(label) });
  };
  if (agrNos.length) {
    const { data: parts } = await supabase.from("mirror_agr_parts").select("part, quantity").eq("custno", custno).in("agrmt_no", agrNos);
    for (const p of (parts ?? []) as { part: string; quantity: number | null }[]) add(p.part.trim().toUpperCase(), Number(p.quantity) || 1);
  }
  for (const a of (acc.data ?? []) as { label: string; qty: number; kind: string | null }[]) add(a.label, a.qty || 1, (a.kind as PartKind) ?? undefined);
  for (const s of (sales.data ?? []) as { locno: string | null; item_lines: { name: string; qty: number }[] | null }[]) {
    if (locno && s.locno && s.locno !== locno) continue;
    for (const l of s.item_lines ?? []) add(l.name, l.qty || 1);
  }
  const charge: TicketLine[] = [];
  for (const a of (adj.data ?? []) as { kind: string; label: string; amount: number; effective: string; locno: string | null }[]) {
    if (locno && a.locno && a.locno !== locno) continue;
    if (a.kind === "charge_visit") charge.push({ label: a.label, amount: Number(a.amount) });
    if (a.kind === "add" && a.effective === "now") add(a.label, 1);
  }
  const t: TicketPreview = { check: null, when: null, bring: [...bring.values()], charge, offer: [] };
  return { note: ticketNoteLine(t), parts: t.bring };
}

// ─── small words ─────────────────────────────────────────────────────────────
const norm = (s: string) => (s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
export const money = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: Number.isInteger(v) ? 0 : 2 });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function longDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${MONTHS[parseInt(m, 10) - 1] ?? m} ${parseInt(d, 10)}, ${y}`;
}
function titleWords(s: string): string {
  return s.length > 3 && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s;
}
function shortDate(iso: string): string {
  const d = iso.slice(0, 10).split("-");
  return `${MONTHS[parseInt(d[1], 10) - 1]} ${parseInt(d[2], 10)}`;
}
